/**
 * ═══════════════════════════════════════════════════════════════════════
 * SignupPage — Unified Registration
 * ═══════════════════════════════════════════════════════════════════════
 *
 * Flow:
 *   1. Pick userType (INDIVIDUAL / ORGANISATION)
 *   2. Pick loginMethod (PHONE / EMAIL)
 *   3. Enter identifier (phone or email)
 *   4. Call authApi.signup() → get { session, username }
 *   5. Enter OTP
 *   6. Call authApi.verifyOtp() → authenticated (or needs password)
 *   7. (Optional) Set password via authApi.setPassword()
 *   8. Store session, route to agent page
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { signup, verifyOtp, setPassword as setPasswordApi } from '../services/auth/authApi';
import { getAuthErrorMessage } from '../services/auth/authErrors';
import { normalizeIndianMobile, validationMessage } from '../utils/phone';
import { TIMING } from '../constants';
import { ROUTES } from '../constants/routes';

import AuthCard from '../components/auth/AuthCard';
import UserTypeSelector from '../components/auth/UserTypeSelector';
import LoginMethodSelector from '../components/auth/LoginMethodSelector';
import PhoneInput from '../components/auth/PhoneInput';
import EmailInput from '../components/auth/EmailInput';
import OtpInput from '../components/auth/OtpInput';
import PasswordInput from '../components/auth/PasswordInput';
import PrimaryButton from '../components/auth/PrimaryButton';
import ResendOtp from '../components/auth/ResendOtp';

// ── Helpers ──────────────────────────────────────────────────────────

function validateEmail(email) {
  if (!email) return 'Enter your email';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return 'Enter a valid email';
  return '';
}

function validatePassword(pw) {
  if (!pw) return 'Enter a password';
  if (pw.length < 8) return 'At least 8 characters';
  if (!/[A-Z]/.test(pw)) return 'Include an uppercase letter';
  if (!/[a-z]/.test(pw)) return 'Include a lowercase letter';
  if (!/\d/.test(pw)) return 'Include a number';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Include a special character';
  return '';
}

// ── Component ────────────────────────────────────────────────────────

export default function SignupPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const allowIndividual = searchParams.get('mode') === 'individual';

  // ── local form state ─────────────────────────────────────────────
  // Steps: 'identifier' → 'otp' → 'password' (optional) → done
  const [step, setStep] = useState('identifier');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLeft, setResendLeft] = useState(0);

  const { userType, loginMethod, setUserType, setLoginMethod, setUsername, setSession } = auth;

  // ── validation ───────────────────────────────────────────────────
  const phoneError = useMemo(
    () => (loginMethod === 'PHONE' && step === 'identifier' ? validationMessage(phone) : ''),
    [phone, loginMethod, step],
  );
  const emailError = useMemo(
    () => (loginMethod === 'EMAIL' && step === 'identifier' ? validateEmail(email) : ''),
    [email, loginMethod, step],
  );
  const passwordError = useMemo(() => (step === 'password' ? validatePassword(password) : ''), [password, step]);
  const confirmError = useMemo(
    () => (step === 'password' && confirmPassword && confirmPassword !== password ? 'Passwords do not match' : ''),
    [password, confirmPassword, step],
  );

  const identifierValid =
    loginMethod === 'PHONE' ? !phoneError && phone.length > 0 : !emailError && email.length > 0;

  // ── resend countdown ─────────────────────────────────────────────
  useEffect(() => {
    if (step === 'otp' && resendLeft > 0) {
      const id = setInterval(() => setResendLeft((s) => Math.max(0, s - 1)), 1000);
      return () => clearInterval(id);
    }
  }, [step, resendLeft]);

  // ── Step 1: send signup OTP ──────────────────────────────────────
  const handleContinue = async () => {
    setError('');
    if (!identifierValid) return;
    setLoading(true);
    try {
      const payload = { userType, loginMethod };
      if (loginMethod === 'PHONE') {
        const { e164 } = normalizeIndianMobile(phone);
        payload.phone = e164;
      } else {
        payload.email = email.trim();
      }

      const res = await signup(payload);

      setUsername(res.username || payload.phone || payload.email);
      setSession(res.session);

      setStep('otp');
      setResendLeft(TIMING.RESEND_OTP_COOLDOWN);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: verify OTP ───────────────────────────────────────────
  const handleVerifyOtp = async () => {
    setError('');
    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      const result = await verifyOtp({
        userType,
        username: auth.username,
        otp,
        session: auth.session,
      });

      // If backend says we still need to set password
      if (result.requiresPassword || result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        if (result.session) setSession(result.session);
        setStep('password');
        setLoading(false);
        return;
      }

      // Otherwise fully authenticated — finish up
      await completeAuth(result);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3 (optional): set password ──────────────────────────────
  const handleSetPassword = async () => {
    setError('');
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const result = await setPasswordApi({
        userType,
        username: auth.username,
        password,
        session: auth.session,
      });

      await completeAuth(result);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  // ── Shared post-auth routine ─────────────────────────────────────
  const completeAuth = async (result) => {
    const { clearStoredSession, storeSession, getSession } = await import(
      '../services/api/sessionApi'
    );
    clearStoredSession();

    // Store JWT in memory so apiClient attaches it to subsequent requests
    if (result?.idToken) {
      const { setAuthToken } = await import('../services/api/apiClient');
      setAuthToken(result.idToken);
    }

    // Session metadata is populated below from /api/session — the
    // canonical source. We intentionally do NOT call storeSession(result)
    // here because the login/verify-otp response carries userId =
    // <raw identifier the user typed> while /api/session returns userId =
    // <canonical user_id from user_profiles>. Writing twice with two
    // different string formats for the same person used to confuse the
    // F1 identity guard.

    await new Promise((r) => setTimeout(r, TIMING.SESSION_STABILIZATION_DELAY));

    let sessionData;
    try {
      sessionData = await getSession();
    } catch (_) {
      // Network error / 5xx after a successful signup. We previously fell
      // back to getStoredSession() — but with storeSession(result) gone
      // there's nothing in localStorage to read. Surface the error so the
      // user retries (signup itself succeeded; they can just log in).
      throw new Error('Unable to load session data');
    }

    storeSession(sessionData);
    auth.loginSuccess({ accessToken: result?.accessToken || null });

    // Start proactive token refresh timer
    try {
      const { scheduleTokenRefresh } = await import('../services/api/apiClient');
      scheduleTokenRefresh();
    } catch (_) { /* non-critical */ }

    if (sessionData.agent === 'crowchat') {
      navigate(ROUTES.ORG, { replace: true });
    } else {
      navigate(ROUTES.CHAT, { replace: true });
    }
  };

  // ── resend OTP ───────────────────────────────────────────────────
  const handleResend = async () => {
    setError('');
    setOtp('');
    if (resendLeft > 0) return;
    try {
      const payload = { userType, loginMethod };
      if (loginMethod === 'PHONE') {
        const { e164 } = normalizeIndianMobile(phone);
        payload.phone = e164;
      } else {
        payload.email = email.trim();
      }

      const res = await signup(payload);
      if (res.session) setSession(res.session);
      setResendLeft(TIMING.RESEND_OTP_COOLDOWN);
    } catch (e) {
      setError(getAuthErrorMessage(e));
    }
  };

  // ── reset when selectors change ──────────────────────────────────
  const handleUserTypeChange = (val) => {
    setUserType(val);
    resetForm();
  };
  const handleLoginMethodChange = (val) => {
    setLoginMethod(val);
    resetForm();
  };
  const resetForm = () => {
    setStep('identifier');
    setPhone('');
    setEmail('');
    setOtp('');
    setPassword('');
    setConfirmPassword('');
    setError('');
    setResendLeft(0);
  };

  // ── render ───────────────────────────────────────────────────────
  const identifierLabel = loginMethod === 'PHONE' ? phone : email;

  return (
    <AuthCard title="Sign Up" subtitle="Create a new account" error={error}>
      <UserTypeSelector
        value={userType}
        onChange={handleUserTypeChange}
        disabled={loading}
        allowIndividual={allowIndividual}
      />
      <LoginMethodSelector
        value={loginMethod}
        onChange={handleLoginMethodChange}
        disabled={loading || step !== 'identifier'}
      />

      {/* ── Step 1: identifier ──────────────────────────────────── */}
      {step === 'identifier' && (
        <>
          {loginMethod === 'PHONE' ? (
            <PhoneInput value={phone} onChange={setPhone} error={phoneError} disabled={loading} />
          ) : (
            <EmailInput value={email} onChange={setEmail} error={emailError} disabled={loading} />
          )}
          <div style={{ height: 8 }} />
          <PrimaryButton onClick={handleContinue} disabled={!identifierValid || loading}>
            {loading ? 'Please wait…' : 'Continue'}
          </PrimaryButton>
        </>
      )}

      {/* ── Step 2: OTP ─────────────────────────────────────────── */}
      {step === 'otp' && (
        <>
          <div style={{ marginBottom: 6, fontSize: 13, color: theme.auth.labelColor }}>
            We sent an OTP to <strong>{identifierLabel}</strong>
          </div>
          <OtpInput value={otp} onChange={setOtp} disabled={loading} />
          <div style={{ height: 8 }} />
          <PrimaryButton onClick={handleVerifyOtp} disabled={loading || otp.length !== 6}>
            {loading ? 'Verifying…' : 'Verify OTP'}
          </PrimaryButton>
          <ResendOtp onClick={handleResend} disabled={resendLeft > 0 || loading} seconds={resendLeft} />
        </>
      )}

      {/* ── Step 3: set password ────────────────────────────────── */}
      {step === 'password' && (
        <>
          <div style={{ marginBottom: 10, fontSize: 13, color: theme.auth.labelColor }}>
            Set a password for your account
          </div>
          <PasswordInput
            value={password}
            onChange={setPassword}
            error={passwordError}
            disabled={loading}
            label="New Password"
            placeholder="Create a strong password"
          />
          <div style={{ height: 8 }} />
          <PasswordInput
            value={confirmPassword}
            onChange={setConfirmPassword}
            error={confirmError}
            disabled={loading}
            label="Confirm Password"
            placeholder="Re-enter password"
          />
          <div style={{ height: 8 }} />
          <PrimaryButton
            onClick={handleSetPassword}
            disabled={loading || !!passwordError || !!confirmError || !password || !confirmPassword}
          >
            {loading ? 'Setting password…' : 'Set Password'}
          </PrimaryButton>
        </>
      )}

      {/* footer link */}
      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 12, color: theme.auth.mutedText }}>
        Already have an account?{' '}
        <Link to={`${ROUTES.LOGIN}${allowIndividual ? '?mode=individual' : ''}`} style={{ color: theme.auth.buttonPrimary, textDecoration: 'none', fontWeight: 600 }}>
          Log In
        </Link>
      </div>
    </AuthCard>
  );
}
